import { Injectable, inject } from "@angular/core";
import { Router } from "@angular/router";
import { AuthManager } from "@infra-adapters/services/auth.service";
import { RegistrationReviewNotificationsService } from "@infra-adapters/services/registration-review-notifications.service";

/**
 * Saca al usuario al login cuando la sesión JWT venció o el API respondió 401.
 * Un solo redirect a la vez: evita quedarse en el shell con errores que parecen
 * “el servidor murió”.
 */
@Injectable({
  providedIn: "root",
})
export class SessionExpiryRedirectService {
  private readonly auth = inject(AuthManager);
  private readonly router = inject(Router);
  private readonly notifications = inject(
    RegistrationReviewNotificationsService,
  );

  private redirecting = false;
  private expiryTimer: ReturnType<typeof setTimeout> | null = null;

  /** Programa la salida automática según `exp` del JWT. */
  armWatch(): void {
    this.clearWatch();
    const delayMs = this.auth.msUntilExpiry();
    if (delayMs == null) {
      return;
    }
    if (delayMs <= 0) {
      this.redirectBecauseExpired();
      return;
    }
    this.expiryTimer = setTimeout(() => {
      this.redirectBecauseExpired();
    }, delayMs);
  }

  clearWatch(): void {
    if (this.expiryTimer != null) {
      clearTimeout(this.expiryTimer);
      this.expiryTimer = null;
    }
  }

  /** Tras un login correcto: permite un nuevo ciclo de vigilancia. */
  resetAfterLogin(): void {
    this.redirecting = false;
    this.armWatch();
  }

  redirectBecauseExpired(returnUrl?: string): void {
    if (this.redirecting) {
      return;
    }
    const current = this.router.url ?? "";
    if (current.startsWith("/login")) {
      this.clearWatch();
      this.notifications.disconnect();
      this.auth.cleanSession();
      return;
    }

    this.redirecting = true;
    this.clearWatch();
    this.notifications.disconnect();
    this.auth.cleanSession();

    const from = returnUrl ?? current;
    const safeReturn =
      from && !from.startsWith("/login") ? from : undefined;

    // Fuera del ciclo de navegación actual: si no, Angular ignora el navigate.
    setTimeout(() => {
      void this.router.navigate(["/login"], {
        queryParams: {
          sessionExpired: "1",
          ...(safeReturn ? { returnUrl: safeReturn } : {}),
        },
        replaceUrl: true,
      });
    }, 0);
  }
}
